// Cross-tab-safe credential store + refresh coordinator for auth v2 (web).
// Pure JS — no React — so it can be unit-tested in Node with fake globals.
export const STORAGE_KEY = 'founderos_auth_v1';
export const IMP_KEY = 'founderos_impersonation_v1';
const LOCK_NAME = 'founderos-auth-refresh';
const LEASE_KEY = 'founderos_auth_refresh_lease';
const REQUEST_TIMEOUT_MS = 15000;
const RETRYABLE_4XX = new Set([408, 409, 425, 429]);

export const BACKOFF_MS = [2000, 5000, 10000, 20000, 30000, 60000];
export const backoffFor = (attempt) => BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const readStored = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
};
export const writeStored = (next) => localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
export const clearStored = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(IMP_KEY);
};

// Auth epoch — bumped on every login / logout / account switch in THIS tab.
// A refresh that started under an older epoch must never commit its result.
let epoch = 0;
export const bumpEpoch = () => { epoch += 1; return epoch; };
export const currentEpoch = () => epoch;

export const tokenExpiresAt = (jwt) => {
  try {
    const b64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const p = JSON.parse(atob(b64));
    return p.exp ? p.exp * 1000 : null;
  } catch { return null; }
};

// 'ok' | 'rejected' (definitive auth failure) | 'transient' (retry later).
export const classifyResponse = (res) => {
  if (res.ok) return 'ok';
  if (res.status >= 500 || RETRYABLE_4XX.has(res.status)) return 'transient';
  return 'rejected';
};

export async function fetchWithTimeout(url, opts = {}, ms = REQUEST_TIMEOUT_MS) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try { return await fetch(url, { ...opts, signal: ctrl.signal }); } finally { clearTimeout(t); }
}

const readLease = () => {
  try { return JSON.parse(localStorage.getItem(LEASE_KEY) || 'null'); } catch { return null; }
};

// Fallback for browsers without Web Locks: a short localStorage lease.
async function withStorageLease(fn) {
  const me = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const deadline = Date.now() + 6000;
  while (Date.now() < deadline) {
    const cur = readLease();
    if (!cur || cur.until < Date.now()) {
      localStorage.setItem(LEASE_KEY, JSON.stringify({ owner: me, until: Date.now() + 10000 }));
      await sleep(20);
      if (readLease()?.owner === me) {
        try { return await fn(); } finally { if (readLease()?.owner === me) localStorage.removeItem(LEASE_KEY); }
      }
    }
    await sleep(80);
  }
  // Waited too long — proceed anyway; the server's reuse grace absorbs the residual race.
  return fn();
}

export const withRefreshLock = (fn) => (
  typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request(LOCK_NAME, fn)
    : withStorageLease(fn)
);

async function callRefresh(API, refreshToken) {
  try {
    const res = await fetchWithTimeout(`${API}/api/auth/v2/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const kind = classifyResponse(res);
    if (kind === 'ok') {
      const data = await res.json();
      return data?.access_token ? { status: 'ok', data } : { status: 'transient' };
    }
    return { status: kind, http: res.status };
  } catch { return { status: 'transient' }; }
}

/**
 * Rotate the session under the cross-tab lock. After acquiring the lock the
 * current credentials are re-read: if another tab already rotated, its result
 * is adopted without a network call. The result is committed only if the
 * epoch and the stored refresh token are unchanged (no logout / account switch
 * happened underneath).
 *
 * Returns { status: 'ok', token, stored } | { status: 'none' } | { status: 'stale' }
 *       | { status: 'rejected', http } | { status: 'transient', http? }.
 */
export async function rotateSession(API, startEpoch, { minTtlMs = 60000, staleToken = null } = {}) {
  const before = readStored();
  if (!before?.refresh_token) return { status: 'none' };
  const fresher = (stored) => {
    // Another tab already rotated past the token we hold → adopt without a network call.
    if (!stored?.token || stored.token === staleToken) return false;
    if (staleToken == null && stored.refresh_token === before.refresh_token) return false;
    const exp = tokenExpiresAt(stored.token);
    return !exp || exp - Date.now() > minTtlMs;
  };
  return withRefreshLock(async () => {
    if (currentEpoch() !== startEpoch) return { status: 'stale' };
    const stored = readStored();
    if (!stored?.refresh_token) return { status: 'none' };
    if (fresher(stored)) return { status: 'ok', token: stored.token, stored };
    let result = { status: 'transient' };
    for (let attempt = 0; attempt < 3; attempt += 1) {
      result = await callRefresh(API, stored.refresh_token);
      if (!(result.status === 'transient' && result.http === 409)) break;
      await sleep(500 * (attempt + 1));
      const again = readStored();
      if (fresher(again)) return { status: 'ok', token: again.token, stored: again };
    }
    if (result.status !== 'ok') return result;
    const cur = readStored();
    if (currentEpoch() !== startEpoch || !cur || cur.refresh_token !== stored.refresh_token) {
      return cur?.token ? { status: 'ok', token: cur.token, stored: cur } : { status: 'stale' };
    }
    const next = { ...cur, token: result.data.access_token, refresh_token: result.data.refresh_token };
    writeStored(next);
    return { status: 'ok', token: next.token, stored: next };
  });
}
